import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeftIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

/**
 * TutorShell — the shared full-screen workspace for all six AI Tutor tools.
 *
 * Replaces the `max-w-4xl mx-auto h-[calc(100vh-120px)]` block that every tool
 * page carried its own copy of. Two things were wrong with that block:
 *
 *   1. `max-w-4xl` capped the workspace at 896px. On a 1920px display that
 *      left roughly half the viewport as empty gutter while the conversation
 *      squeezed into the middle — the "congested" look.
 *   2. `calc(100vh-120px)` hardcoded a guess at the surrounding chrome. These
 *      pages render in TWO places (the learner app at /tsg/*, and TSG Nexus at
 *      /nextgen-builder/ai-tutor/*) whose headers are different heights, and
 *      neither is 120px at every breakpoint. So the panel was either short of
 *      the fold or overflowing it, depending on where you looked at it.
 *
 * This shell fixes both: it spans the full width, and it measures its own
 * distance to the bottom of the viewport instead of guessing (see
 * useAvailableHeight). Every tool keeps its own logic — the shell is layout
 * only and holds no state beyond the mobile rail toggle.
 */

export const TUTOR = {
  bg: "#F6F7FB",
  border: "#E9E7F3",
  brand: "#5B21B6",
  brandSoft: "#F5F2FF",
  text: "#111827",
  muted: "#6B7280",
  faint: "#94A3B8",
  gradient: "linear-gradient(135deg,#5B21B6 0%,#7C3AED 55%,#A855F7 100%)",
};

/**
 * Measure the real space between the top of the shell and the bottom of the
 * viewport, rather than assuming a fixed amount of chrome sits above it.
 *
 * This is what makes one component work correctly in both surfaces without a
 * mode flag: whatever is rendered above — the TSG Nexus header, nothing at
 * all, or something added later — the shell simply takes what is left. It
 * re-measures on resize and when the element above it changes height.
 */
export const useAvailableHeight = (minHeight = 420) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;
    const top = el.getBoundingClientRect().top;
    const next = Math.max(minHeight, Math.round(window.innerHeight - top));
    setHeight((prev) => (prev === next ? prev : next));
  }, [minHeight]);

  useLayoutEffect(() => {
    measure();
    if (typeof window === "undefined") return undefined;
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      if (ref.current?.parentElement) observer.observe(ref.current.parentElement);
      // Also watch the whole document: the TSG Nexus header above this shell
      // renders AFTER auth resolves, pushing the shell down without resizing
      // its own parent. Measuring only once (before the header appeared) left
      // the composer exactly one header-height below the fold — the "have to
      // scroll down to reach the text bar" bug.
      if (typeof document !== "undefined" && document.body) observer.observe(document.body);
    }
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      if (observer) observer.disconnect();
    };
  }, [measure]);

  return [ref, height];
};

/* ------------------------------ Rail pieces ------------------------------ */

export const RailSection = ({ title, children, className = "" }) => (
  <div className={`px-5 py-4 ${className}`}>
    {title ? (
      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#94A3B8] mb-3">
        {title}
      </p>
    ) : null}
    {children}
  </div>
);

export const RailStat = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 mb-2 last:mb-0">
    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">{label}</p>
    <p className="text-sm font-black text-[#111827] mt-1 break-words">{value}</p>
    {hint ? <p className="text-[11px] font-medium text-[#94A3B8] mt-1 leading-snug">{hint}</p> : null}
  </div>
);

export const RailTip = ({ children }) => (
  <div className="rounded-2xl bg-[#F5F2FF] border border-[#E9E7F3] px-4 py-3 mb-2 last:mb-0">
    <p className="text-[12px] font-medium text-[#4B3F72] leading-relaxed">{children}</p>
  </div>
);

/* -------------------------------- Shell ---------------------------------- */

const TutorShell = ({
  icon,
  title,
  subtitle,
  status = "AI Assistant Online",
  // Rendered at the left of the header badges. Every tool passes the same
  // <LanguagePicker>, so the control sits in one fixed place across all six.
  language = null,
  badges = null,
  // Optional slot rendered at the right of the header, before the badges.
  // TSG Nexus puts the feature-billing allowance chip here; every other
  // surface passes nothing and the header renders exactly as before.
  headerExtra = null,
  rail = null,
  railTitle = "Session",
  onBack,
  children,
  composer = null,
  footerNote = "AI-POWERED BY THE SKILLGEN ELITE MASTERY SYSTEM",
}) => {
  const [outerRef, height] = useAvailableHeight();
  const [railOpen, setRailOpen] = useState(false);

  const hasRail = Boolean(rail);

  const railBody = (
    <>
      <div className="px-5 pt-5 pb-4 border-b border-[#E9E7F3]">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-purple-200"
            style={{ background: TUTOR.gradient }}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <h1 className="text-[15px] font-black tracking-tight text-[#111827] truncate">{title}</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shrink-0" />
              <span className="text-[9px] font-black uppercase tracking-[0.12em] text-[#5B21B6] truncate">
                {status}
              </span>
            </div>
          </div>
        </div>
        {subtitle ? (
          <p className="text-[12px] font-medium text-[#6B7280] leading-relaxed mt-3">{subtitle}</p>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pb-4">{rail}</div>

      <div className="px-5 py-3 border-t border-[#E9E7F3]">
        <p className="text-[9px] font-black text-[#CBD5E1] uppercase tracking-[0.12em] leading-relaxed">
          {footerNote}
        </p>
      </div>
    </>
  );

  return (
    <div
      ref={outerRef}
      className="w-full flex bg-[#F6F7FB] text-[#111827] font-sans overflow-hidden"
      style={height ? { height } : { minHeight: "60vh" }}
    >
      {/* Desktop rail */}
      {hasRail ? (
        <aside className="hidden lg:flex w-[292px] xl:w-[320px] shrink-0 flex-col bg-white border-r border-[#E9E7F3]">
          {railBody}
        </aside>
      ) : null}

      {/* Mobile rail drawer */}
      {hasRail && railOpen ? (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setRailOpen(false)}
          />
          <aside className="relative z-10 w-[86%] max-w-[320px] flex flex-col bg-white shadow-2xl animate-in slide-in-from-left duration-200">
            <button
              onClick={() => setRailOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-xl border border-[#E9E7F3] flex items-center justify-center hover:bg-[#F5F2FF]"
              aria-label="Close panel"
            >
              <XMarkIcon className="w-4 h-4 text-[#5B21B6]" />
            </button>
            {railBody}
          </aside>
        </div>
      ) : null}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="shrink-0 h-[60px] px-4 md:px-6 flex items-center justify-between gap-3 bg-white/85 backdrop-blur border-b border-[#E9E7F3]">
          <div className="flex items-center gap-2.5 min-w-0">
            {onBack ? (
              <button
                onClick={onBack}
                title="Go Back"
                className="w-9 h-9 shrink-0 rounded-xl border border-[#E9E7F3] bg-white flex items-center justify-center hover:bg-[#F5F2FF] hover:border-[#5B21B6] transition-all group"
              >
                <ChevronLeftIcon className="w-4 h-4 text-[#5B21B6] group-hover:-translate-x-0.5 transition-transform" />
              </button>
            ) : null}

            {hasRail ? (
              <button
                onClick={() => setRailOpen(true)}
                title={railTitle}
                className="lg:hidden w-9 h-9 shrink-0 rounded-xl border border-[#E9E7F3] bg-white flex items-center justify-center hover:bg-[#F5F2FF]"
              >
                <Bars3Icon className="w-4 h-4 text-[#5B21B6]" />
              </button>
            ) : null}

            {/* Title repeats here only where the rail is hidden, so small
                screens never lose the name of the tool they are in. */}
            <div className={hasRail ? "lg:hidden min-w-0" : "min-w-0"}>
              <p className="text-[14px] font-black tracking-tight text-[#111827] truncate">{title}</p>
            </div>
          </div>

          {language || badges || headerExtra ? (
            <div className="flex items-center gap-2 shrink-0">
              {headerExtra}
              {language}
              {badges}
            </div>
          ) : null}
        </header>

        <main className="flex-1 min-h-0 overflow-hidden">{children}</main>

        {composer ? (
          <div className="shrink-0 border-t border-[#E9E7F3] bg-white/90 backdrop-blur px-4 md:px-6 py-3">
            {composer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TutorShell;
